import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITIES = ["Bern", "Basel", "Lausanne"];

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", location: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        location: form.location || null,
        message: form.message.trim(),
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", location: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-36 bg-anthracite text-cream overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/4 w-[700px] h-[400px]" style={{ background: "radial-gradient(ellipse, rgba(198,156,34,0.15) 0%, transparent 60%)" }} />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[500px] h-[300px]" style={{ background: "radial-gradient(ellipse, rgba(198,156,34,0.08) 0%, transparent 60%)" }} />
      <div className="container-boe grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5">
          <span className="section-eyebrow">{t.contact.eyebrow}</span>
          <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold text-cream leading-[1.05] tracking-tight">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-lg text-cream/80 leading-relaxed max-w-md">
            {t.contact.subtitle}
          </p>

          <div className="mt-10 space-y-4 text-cream/80">
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              <span className="text-sm">Bern · Basel · Lausanne</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              <span className="text-sm">@boe.swiss · Instagram</span>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          data-testid={CONTACT.form}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 bg-anthracite-soft rounded-3xl border border-gold/15 p-8 lg:p-12 shadow-xl shadow-black/30"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <label className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/60">
                {t.contact.name}
              </span>
              <Input
                value={form.name}
                onChange={update("name")}
                required
                minLength={1}
                data-testid={CONTACT.name}
                className="h-12 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/60">
                {t.contact.email}
              </span>
              <Input
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                data-testid={CONTACT.email}
                className="h-12 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60"
              />
            </label>
          </div>

          <label className="flex flex-col gap-2 mt-5">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/60">
              {t.contact.location}
            </span>
            <select
              value={form.location}
              onChange={update("location")}
              data-testid={CONTACT.location}
              className="h-12 rounded-xl border border-white/15 bg-anthracite text-cream px-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold/60"
            >
              <option value="">{t.contact.pickCity}</option>
              {CITIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-2 mt-5">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/60">
              {t.contact.message}
            </span>
            <Textarea
              value={form.message}
              onChange={update("message")}
              required
              minLength={1}
              rows={5}
              data-testid={CONTACT.message}
              className="rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60 resize-none"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid={CONTACT.submit}
            className="btn-primary mt-8 w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t.contact.sending}
              </>
            ) : (
              <>
                <Send size={18} />
                {t.contact.send}
              </>
            )}
          </button>

          <p className="mt-6 text-xs text-cream/55 leading-relaxed max-w-lg">
            {t.contact.privacy}
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
